import { useEffect, useState } from 'react';
import type { ImageItem } from '../../doc/schema';
import { useAssetUrl } from '../assets/localAssets';
import { pickVariant } from '../assets/variants';
import { usePane } from '../context';

/**
 * Picks the image variant from the on-screen size at the last settled zoom. A new variant is
 * decoded off-screen first and only swapped in once ready, so zooming never flashes a blank
 * or half-loaded image.
 */
export function ImageBody({ item, width }: { item: ImageItem; width: number }) {
  const zoom = usePane((s) => s.settledZoom);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const longestSide = Math.max(width, (width * item.props.naturalH) / Math.max(1, item.props.naturalW));
  const variant = pickVariant(longestSide * zoom * dpr);
  const url = useAssetUrl(item.props.assetId, variant);
  const [shown, setShown] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    const done = () => {
      if (!cancelled) setShown(url);
    };
    if (typeof img.decode === 'function') img.decode().then(done, done);
    else {
      img.onload = done;
      img.onerror = done;
    }
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!shown) return <div className="image-placeholder" />;
  return (
    <img
      className="image-img"
      src={shown}
      alt=""
      draggable={false}
      style={{ opacity: item.props.opacity }}
      decoding="async"
    />
  );
}
